import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { User, Task } from '../../types';
import { usersAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import UserPerformanceChart from './UserPerformanceChart';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

interface UserPerformance {
  user: User;
  taskStats: Record<string, number>;
  priorityStats: Record<string, number>;
  recentTasks: Task[];
  teams: any[];
}

interface UserStatistics {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  createdTasks: number;
  managedTeams: number;
  memberOfTeams: number;
  completionRate: number;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ isOpen, onClose, user }) => {
  const [performance, setPerformance] = useState<UserPerformance | null>(null);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'teams'>('overview');

  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
    }
  }, [isOpen, user]); // loadUserData is defined inside the effect scope

  const loadUserData = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const [performanceData, statisticsData] = await Promise.all([
        usersAPI.getUserPerformance(user.id),
        usersAPI.getUserStatistics(user.id),
      ]);
      setPerformance(performanceData);
      setStatistics(statisticsData);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!user) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-6xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-white">
                      {user.firstName[0]}{user.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.firstName} {user.lastName}
                    </h2>
                    <p className="text-gray-600">{user.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon },
                    { id: 'teams', name: 'Teams', icon: UserGroupIcon },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Content */}
              <div className="min-h-96">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <div>
                    {activeTab === 'overview' && statistics && (
                      <div className="space-y-6">
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-blue-600">Total Tasks</p>
                                <p className="text-2xl font-bold text-blue-900">{statistics.totalTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <CheckCircleIcon className="w-8 h-8 text-green-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-green-600">Completed</p>
                                <p className="text-2xl font-bold text-green-900">{statistics.completedTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-red-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-red-600">Overdue</p>
                                <p className="text-2xl font-bold text-red-900">{statistics.overdueTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <UserGroupIcon className="w-8 h-8 text-purple-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-purple-600">Teams</p>
                                <p className="text-2xl font-bold text-purple-900">{statistics.memberOfTeams}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Performance Charts */}
                        {performance && (
                          <UserPerformanceChart
                            taskStats={performance.taskStats}
                            priorityStats={performance.priorityStats}
                          />
                        )}

                        {/* Completion Rate */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                              <div className="mt-2">
                                <div className="flex items-center">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(statistics.completionRate, 100)}%` }}
                                    />
                                  </div>
                                  <span className="ml-2 text-sm font-medium text-gray-900">
                                    {statistics.completionRate}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Tasks Created</p>
                              <p className="text-xl font-bold text-gray-900">{statistics.createdTasks}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Teams Managed</p>
                              <p className="text-xl font-bold text-gray-900">{statistics.managedTeams}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'tasks' && performance && (
                      <div className="space-y-6">
                        {/* Task Status Distribution */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(performance.taskStats).map(([status, count]) => (
                              <div key={status} className="text-center">
                                <div className="text-2xl font-bold text-gray-900">{count}</div>
                                <div className={`text-sm px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(status)}`}>
                                  {status.replace('_', ' ')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Recent Tasks */}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Tasks</h3>
                          <div className="space-y-3">
                            {performance.recentTasks.length === 0 ? (
                              <p className="text-gray-500 text-center py-8">No recent tasks</p>
                            ) : (
                              performance.recentTasks.map((task) => (
                                <div key={task.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                                      <div>
                                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                                        <p className="text-sm text-gray-500">
                                          {task.team?.name || 'No team'} • Updated {new Date(task.updatedAt).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                                      {task.status.replace('_', ' ')}
                                    </span>
                                  </div>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{task.description}</p>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'teams' && performance && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Team Memberships</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {performance.teams.length === 0 ? (
                              <p className="text-gray-500 text-center py-8 col-span-full">Not a member of any teams</p>
                            ) : (
                              performance.teams.map((team) => (
                                <div key={team.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                                  <div className="flex items-center space-x-3 mb-3">
                                    <div
                                      className="w-4 h-4 rounded-full"
                                      style={{ backgroundColor: team.color }}
                                    />
                                    <h4 className="font-medium text-gray-900">{team.name}</h4>
                                  </div>
                                  {team.description && (
                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{team.description}</p>
                                  )}
                                  <div className="flex justify-between text-sm text-gray-500">
                                    <span>{team._count?.tasks || 0} tasks</span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UserDetailModal;
